/**
 * This utils cover basic functionality:
 * detect safari browser and standalone mode
 * once you need more sophisticated UA parsing, check:
 * https://github.com/faisalman/ua-parser-js/blob/master/src/main/ua-parser.js
 * https://github.com/philfung/add-to-homescreen/blob/main/src/index.ts
 */

function _matchesUserAgent(regex: RegExp): boolean {
    return !!window.navigator.userAgent.match(regex)
}

/* Mozilla/5.0 (iPhone; CPU iPhone OS 10_3 like Mac OS X)
     AppleWebKit/602.1.50 (KHTML, like Gecko) CriOS/56.0.2924.75
     Mobile/14E5239e Safari/602.1 */
export function isBrowserIOSChrome(): boolean {
    return isDeviceIOS() && _matchesUserAgent(/CriOS/)
}

/* Mozilla/5.0 (Macintosh; Intel Mac OS X 10.15; rv:144.0)
Gecko/20100101 Firefox/144.0 */
export function isBrowserFirefox(): boolean {
    return _matchesUserAgent(/Firefox/)
}

/* Mozilla/5.0 (iPhone; CPU iPhone OS 16_5 like Mac OS X)
  AppleWebKit/605.1.15 (KHTML, like Gecko) FxiOS/114.1 Mobile/15E148 Safari/605.1.15 */
function isBrowserIOSFirefox(): boolean {
    return isDeviceIOS() && _matchesUserAgent(/FxiOS/)
}

function isDesktopMac(): boolean {
    return window.navigator.userAgent.includes('Macintosh')
}

export function isDeviceIOS(): boolean {
    return _matchesUserAgent(/iPhone|iPad|iPod/) || isBrowserIOSIPadSafari()
}

function isBrowserIOSIPadSafari(): boolean {
    return !!(
        _matchesUserAgent(/iPad/) || // iPad Mini
        // iPad Air, iPad Pro
        (_matchesUserAgent(/Macintosh/) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1)
    )
}

/* Mozilla/5.0 (iPhone; CPU iPhone OS 10_3 like Mac OS X)
   AppleWebKit/603.1.23 (KHTML, like Gecko) Version/10.0
   Mobile/14E5239e Safari/602.1 */
export function isBrowserIOSSafari(): boolean {
    return isDeviceIOS() && _matchesUserAgent(/Safari/) && !isBrowserIOSChrome() && !isBrowserIOSFirefox()
}

export function isSafari() {
    return (
        window.navigator.userAgent.includes('Safari') &&
        !window.navigator.userAgent.includes('Chrome') &&
        !window.navigator.userAgent.includes('Edg')
    )
}

export function isDesktopSafari(): boolean {
    return isSafari() && isDesktopMac()
}

export function isDesktopChrome(): boolean {
    const isChrome = window.navigator.userAgent.includes('Chrome') && !window.navigator.userAgent.includes('Edg') // Exclude Edge browser
    const isDesktop =
        window.navigator.userAgent.includes('Windows') ||
        window.navigator.userAgent.includes('Macintosh') ||
        window.navigator.userAgent.includes('Linux')

    return isChrome && isDesktop
}

export function isDesktopEdge(): boolean {
    return window.navigator.userAgent.includes('Edg/')
}

function isIOSPWA() {
    return !!('standalone' in window.navigator && window.navigator.standalone)
}

export function compareVersionStrings(version: string, minVersion: string): boolean {
    const parse = (v: string) => v.split('.').map(Number)
    const parts = parse(version)
    const min = parse(minVersion)
    for (let i = 0; i < min.length; i++) {
        if ((parts[i] ?? 0) > (min[i] ?? 0)) {
            return true
        }
        if ((parts[i] ?? 0) < (min[i] ?? 0)) {
            return false
        }
    }
    return true
}

export function isPWARuntimeCheck(): boolean {
    if (typeof window === 'undefined' || !window.navigator) {
        return false
    }
    return (
        isIOSPWA() || // IOS (TODO: detect iPad 13)
        !!window.matchMedia('(display-mode: standalone)').matches || // Android and Desktop Chrome/Safari/Edge
        !!document.referrer.includes('android-app://com.playgama.twa') // TWA Android app
    )
}
